import { SET_TRIMS } from './actionTypes';
import xws from 'api/xws/';

// Selectors
import { getTokenId } from 'store/reducers/tokenId/selectors';
import {
  getVariant,
  getDealerCountryCode,
  getMakeConfig
} from 'store/reducers/dealerSettings/selectors';
import { getWebkey } from 'store/reducers/queryParams/selectors';
import { getLocaleCode } from 'store/reducers/dealerLocale/selectors';
import {
  getYear,
  getModel,
  getVehicle,
  getVehicleId,
  getVin
} from 'store/reducers/appointment/selectors';

// Actions
import { ajaxCallbacks } from 'store/reducers/ajaxCall/actions';
import { setCustomerVehicle } from 'store/reducers/appointment/actions';
import {
  setTrimsSelectedPosition,
  setIdkSelected
} from 'store/reducers/userInteractions/actions';

// Constants
import { getService } from 'constants/ajax';
import { CHANNEL } from 'constants/appConfig';
import { I_DONT_KNOW } from 'constants/vehicle';

// Utils
import { toCapitalize } from 'utils/string';

export const getTrims = intl => async (dispatch, getState) => {
  const webkey = getWebkey(getState());
  const { make } = getMakeConfig(getState());
  const year = getYear(getState());
  const model = getModel(getState());
  const trimsService = getService('trims');
  const languageCode = getLocaleCode(getState()).split('_');

  // this is a required field, so we send "NO_VARIANT" if there's no variant
  // configured for the selected make
  const variant = getVariant(getState()) || 'NO_VARIANT';
  trimsService.setPathVars({ webkey, make, model, year });
  trimsService.setCallBacks(dispatch(ajaxCallbacks()));

  const useSkipTrim = getVin(getState()) || getVehicleId(getState()) ? 0 : 1;

  const {
    data: { vehicleInfo: trimsData }
  } = await xws(trimsService).get(trimsService.getPath(), {
    params: {
      dataStyle: '1',
      channel: CHANNEL,
      tokenId: getTokenId(getState()),
      variant,
      countryCode: getDealerCountryCode(getState()),
      languageCode: languageCode[0],
      locale: getLocaleCode(getState()),
      useSkipTrim,
      addIDK: true
    }
  });
  if (trimsData) {
    const formattedTrimsData = relocateIDKLabel(trimsData, intl);
    dispatch(setTrims(formattedTrimsData));
    dispatch(validateTrimSelection(formattedTrimsData));
    return trimsData;
  }
};

const validateTrimSelection = trimsData => (dispatch, getState) => {
  const vehicleSelected = getVehicle(getState());
  if (!vehicleSelected.engineSize || !trimsData.transmissions) {
    return;
  }

  const driveTypesList = trimsData.transmissions.find(
    t =>
      t.value ===
      (vehicleSelected.transmission || vehicleSelected.transmissionType)
  ).driveTypes;
  const trimsList = driveTypesList.find(
    dt => dt.value === vehicleSelected.driveType
  ).trims;
  if (trimsList.find(trim => trim.value === vehicleSelected.trim)) {
    return;
  }
  // Workaround bug for trim data miss matching usually as 'UNKNOWN'
  let trimIterator = 0;
  const trimsLength = trimsList.length;

  for (trimIterator; trimIterator < trimsLength; trimIterator++) {
    const possibleTypes = trimsList[trimIterator].engineTypes;
    const possibleTypePosition = possibleTypes.findIndex(
      t => t.value === vehicleSelected.engineType
    );
    if (possibleTypePosition > -1) {
      let typeIterator = 0;
      const typesLength = possibleTypes.length;
      for (typeIterator; typeIterator < typesLength; typeIterator++) {
        const possibleSizes = possibleTypes[typeIterator].engineSizes;
        const possibleSizesPosition = possibleSizes.findIndex(
          t => t.value === vehicleSelected.engineSize
        );
        if (possibleSizesPosition > -1) {
          // selecting the correct trim for positions;
          dispatch(setTrimsSelectedPosition(trimIterator));
          dispatch(
            setCustomerVehicle(
              {
                ...vehicleSelected,
                trim: trimsList[trimIterator].value
              },
              {
                preservServices: true
              }
            )
          );
          return;
        }
      }
    }
  }
  // invalid trim selection convert to IDK
  dispatch(setIdkSelected(true));
  dispatch(
    setCustomerVehicle({
      ...vehicleSelected,
      transmission: null,
      driveType: null,
      trim: null,
      engineType: null,
      engineSize: null
    })
  );
};

const relocateIDKLabel = (trimsData, intl) => {
  const { transmissions } = trimsData;
  const validIntl = intl && intl.formatMessage;
  if (!transmissions) {
    return trimsData;
  }
  const IDKLabel = {
    value: '',
    label: validIntl
      ? toCapitalize(
          intl.formatMessage({
            id: 'c8.vehicle.page.i.dont.know',
            default: I_DONT_KNOW.toLowerCase()
          })
        )
      : toCapitalize(I_DONT_KNOW.toLowerCase())
  };
  if (transmissions.length <= 2) {
    transmissions.forEach((transmission, index) => {
      if (transmission.value === '') {
        transmissions.splice(index, 1);
      } else {
        const { driveTypes } = transmission;
        if (driveTypes.length > 1) {
          driveTypes.push(IDKLabel);
        } else {
          driveTypes.forEach(driveType => {
            const { trims } = driveType;
            if (trims.length > 1) {
              trims.push(IDKLabel);
            } else {
              trims.forEach(trim => {
                const { engineTypes } = trim;
                if (engineTypes.length > 1) {
                  engineTypes.push(IDKLabel);
                } else {
                  engineTypes.forEach(engineType => {
                    const { engineSizes } = engineType;
                    if (engineSizes.length > 1) {
                      engineSizes.push(IDKLabel);
                    } else {
                      engineSizes.forEach(engineSize => {
                        const { drivingConditions } = engineSize;
                        if (drivingConditions.length > 1) {
                          drivingConditions.push(IDKLabel);
                        }
                      });
                    }
                  });
                }
              });
            }
          });
        }
      }
    });
  }
  return { transmissions, ...trimsData };
};

export function setTrims(trims) {
  return {
    type: SET_TRIMS,
    payload: trims
  };
}



// WEBPACK FOOTER //
// ./src/store/reducers/trims/actions.js